import {
  Button,
  Group,
  Modal,
  Stack,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useState } from 'react';
import { X } from 'tabler-icons-react';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { UpdateSettlementStatusToDoneSchema } from '../../schemas/update-settlement-status-request.schema';
import { ErrorResponse } from '../../type/error-response.types';
import { UpdateSettlementStatusToDoneRequest } from '../../type/update-settlement-status-request.type';

interface Props {
  isApproveSettlementModalOpen: boolean;
  setIsApproveSettlementModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  settlementId: number;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
}
function ApproveSettlementModal(props: Props) {
  const theme = useMantineTheme();
  const axiosPrivate = useAxiosPrivate();
  const {
    isApproveSettlementModalOpen,
    setIsApproveSettlementModalOpen,
    settlementId,
    setRefreshTable,
  } = props;
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setIsApproveSettlementModalOpen(false);
  };

  const form = useForm<{
    payoutDate: Date | null;
  }>({
    initialValues: {
      payoutDate: null,
    },
    schema: joiResolver(UpdateSettlementStatusToDoneSchema),
  });

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      const request: UpdateSettlementStatusToDoneRequest = {
        settlementId: settlementId,
        payoutDate: form.values.payoutDate!,
      };

      await axiosPrivate.put('web/settlements/status/done', request);
      setRefreshTable((prev) => !prev);
      handleClose();
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-update-settlement-status',
        message:
          error?.error ||
          err?.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        size='xl'
        opened={isApproveSettlementModalOpen}
        centered
        onClose={handleClose}
        closeOnClickOutside={isLoading ? false : true}
        closeOnEscape={isLoading ? false : true}
      >
        <Stack p='xl'>
          <Group>
            <Title>Confirm Payout</Title>
          </Group>

          <form onSubmit={handleSubmit}>
            <DatePicker
              fullWidth
              label='Payout date'
              onChange={(date) => {
                form.setFieldValue('payoutDate', date);
              }}
              dayStyle={(date) =>
                date.getDate() === new Date().getDate() &&
                date.getMonth() === new Date().getMonth()
                  ? {
                      backgroundColor: theme.colors.blue[6],
                      color: theme.white,
                    }
                  : {}
              }
              error={form.errors.payoutDate}
            />
            <Group mt={8} grow>
              <Button
                disabled={!form.values.payoutDate}
                onClick={handleSubmit}
                fullWidth
                loading={isLoading}
              >
                Confirm
              </Button>
            </Group>
          </form>
        </Stack>
      </Modal>
    </>
  );
}

export default ApproveSettlementModal;
